"use client";

import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import CircularButton from "@/components/CircularButton";
import { FlexRowAlignCenter } from "@/components/Flex";
import { ASSETS_URL } from "@/config/deployConstants";
import { iconSizes } from "@/utils/iconProps";
import { themeColors } from "@/utils/themeColors";
import Box from "@mui/material/Box";
import { MediaPlayer, MediaProvider, useMediaRemote } from "@vidstack/react";
import { DefaultVideoLayout, defaultLayoutIcons } from "@vidstack/react/player/layouts/default";
import { DownloadIcon } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useMediaPlayerContextSelector } from "../hooks/useMediaPlayerContext";
import styles from "./videoPlayer.module.css";

const playButton = `${ASSETS_URL}/images/play-icon-white.png`;

export const DownloadButton = ({ src }: { src: string }) => {
    const [downloading, setDownloading] = useState(false);

    return (
        <FlexRowAlignCenter
            as={Box}
            onClick={async () => {
                if (downloading) return;
                setDownloading(true);
                if (!src) return;

                try {
                    const response = await fetch(src);
                    if (!response.ok) throw new Error("Failed to download file");

                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);

                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", src.split("/").pop() || "downloaded-file");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    URL.revokeObjectURL(url); // Cleanup memory
                } catch {
                    toast.error("Download failed, please try again after reload or contact support.");
                } finally {
                    setDownloading(false);
                }
            }}
            sx={{
                gap: "1.5rem",
                backgroundColor: "inherit",
                padding: "1rem",
                cursor: "pointer",
                alignSelf: "flex-start",

                "&:hover": {
                    background: "var(--media-menu-item-hover-bg, rgb(245 245 245 / 0.08));",
                },
                opacity: downloading ? 0.5 : 1,
            }}>
            <DownloadIcon size={iconSizes.SM} />
            {downloading ? "Downloading..." : "Download"}
        </FlexRowAlignCenter>
    );
};
const VideoPlayer = ({
    src,
    title,
    chaptersUrl,
    spriteSheetUrl,
    subtitlesUrl,
    autoplay = false,
    style,
}: {
    src: string;
    title?: string;
    chaptersUrl?: string;
    spriteSheetUrl?: string;
    subtitlesUrl?: string;
    autoplay?: boolean;
    style?: React.CSSProperties;
}) => {
    const mediaPlayerRef = useMediaPlayerContextSelector(state => state.mediaPlayerRef);
    const setMediaPlayerRef = useMediaPlayerContextSelector(state => state.setMediaPlayerRef);

    const remote = useMediaRemote(mediaPlayerRef);

    const [isPlayerInitiated, setIsPlayerInitiated] = useState(false);

    const renderDownloadButton = () => {
        return <DownloadButton src={src} />;
    };

    return (
        <MediaPlayer
            onPlay={() => {
                setIsPlayerInitiated(true);
            }}
            ref={ref => {
                setMediaPlayerRef(ref);
            }}
            title={title ?? ""}
            src={src}
            aspectRatio="16:9"
            autoplay={autoplay}
            load="play"
            liveEdgeTolerance={30}
            className={styles.mediaPlayer}>
            <MediaProvider
                style={{
                    aspectRatio: 16 / 9,
                    backgroundColor: isPlayerInitiated ? "transparent" : themeColors.neutralBlack,
                    width: "100%",
                    border: isPlayerInitiated ? "none" : `1px solid ${themeColors.neutral1}`,
                    ...style,
                }}>
                {subtitlesUrl && (
                    <track
                        src={subtitlesUrl}
                        label="English"
                        srcLang="en-US"
                        kind="subtitles"
                        default
                        data-testid="auto-id-0f41a8de-23c7-47e9-9eaa-50513093ec2b"
                    />
                )}
                {chaptersUrl && (
                    <track
                        src={chaptersUrl}
                        label="English"
                        srcLang="en-US"
                        kind="chapters"
                        default
                        data-testid="auto-id-d6015bc4-15d7-4482-bbff-46e6520f214e"
                    />
                )}
            </MediaProvider>
            {isPlayerInitiated ? (
                <DefaultVideoLayout
                    thumbnails={spriteSheetUrl ?? undefined}
                    className={styles.mediaPlayer}
                    noGestures
                    icons={defaultLayoutIcons}
                    slots={{
                        afterSettingsMenuEndItems: renderDownloadButton(),
                    }}
                />
            ) : (
                <CircularButton
                    radius={"8rem"}
                    onClick={e => {
                        remote.play();
                        e.stopPropagation();
                        setIsPlayerInitiated(true);
                    }}
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 2,
                        backgroundColor: "#f24647",
                    }}>
                    <Image src={playButton} alt={"play"} width={30} height={30} style={{ paddingLeft: "0.5rem" }} />
                </CircularButton>
            )}
            <style data-testid="auto-id-4128f68b-69f1-4686-b401-7993ff88c41e">
                {`
                    .vds-menu-items{
                        --media-menu-y-offset: -377px;
                    }

                    .vds-settings-menu-items{
                        --media-menu-y-offset: -126px;
                    }

                    .vds-menu-items:where(.vds-menu-items:not([data-submenu]):not([data-placement])) {
                        right: calc(-740.333px + 100px) !important;
                        width: 100px !important;
                        top: calc(-297px + calc(-1 * -520px)) !important;
}
                    `}
            </style>
        </MediaPlayer>
    );
};

export default VideoPlayer;
